import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  ArrowDown01,
  ArrowDown10,
  ArrowDownAZ,
  ArrowDownNarrowWide,
  ArrowDownWideNarrow,
  ArrowDownZA,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Edit,
  FileX2,
  Loader,
  LucideAngularModule,
  Minus,
} from 'lucide-angular';
import { RouteParams } from '@app/shared/abstract/route-params';
import { SelectComponent } from '../../form/select/select.component';
import { FormControl } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { PagedEntities } from '@app/shared/abstract/paged-entities';
import { Column } from './column';
import { AbstractEntity } from '@app/shared/abstract/abstract-entity';
import { NgClass, NgComponentOutlet, NgStyle } from '@angular/common';
import { PageMetadata } from '@app/shared/abstract/page-metadata';
import { BadgeComponent } from '@app/shared/components/badge/badge.component';
import { RowAction } from '@app/partials/datatable/data-table/row-action';

@Component({
  selector: 'app-data-table',
  imports: [LucideAngularModule, SelectComponent, RouterLink, NgClass, NgComponentOutlet, BadgeComponent, NgStyle],
  templateUrl: './data-table.component.html',
  styleUrl: './data-table.component.css',
  standalone: true,
})
export class DataTableComponent<T extends AbstractEntity> implements OnChanges, OnInit {
  @Input() dataPage?: PagedEntities<T>;
  @Input() title?: string;
  @Input() columns: Column<T>[] = [];
  @Input() paginate = true;
  @Input() noDataPlaceholder = true;
  @Input() rowActions: RowAction<T>[] = [];
  @Input() rowRouterLinkTemplate?: string;
  @Input() hover = true;
  @Output() route = new EventEmitter<RouteParams>();

  protected showPerPageControl: FormControl = new FormControl(10);
  protected readonly EditIcon = Edit;
  protected readonly FileX2Icon = FileX2;
  protected readonly MinusIcon = Minus;
  protected readonly LoaderIcon = Loader;

  protected colsNum?: number;

  currentPage!: number;
  totalPages!: number;
  startEntry!: number;
  endEntry!: number;
  totalEntries!: number;
  pagesAroundCurrent: number[] = [];

  loading = true;

  routeParams = new RouteParams({
    sort: [],
    filter: [],
    pageNum: 1,
    pageSize: 10,
  });

  perPageChoices = [
    { value: 1, label: '1' },
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' },
  ];

  constructor(private router: Router) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dataPage']) {
      this.loading = false;
      this.colsNum = this.columns.length + (this.rowActions ? 1 : 0);
      this.updatePagination();
    }
  }

  ngOnInit() {
    this.showPerPageControl.valueChanges.subscribe((perPage) => {
      this.routeParams.pageSize = perPage;
      this.route.emit(this.routeParams);
    });
  }

  updatePagination() {
    if (!this.dataPage || !this.paginate) {
      return;
    }
    const meta: PageMetadata = this.dataPage.meta;
    this.currentPage = meta.current_page;
    this.totalPages = meta.last_page;
    this.startEntry = meta.from;
    this.endEntry = meta.to;
    this.totalEntries = meta.total;
    this.pagesAroundCurrent = this.getPagesAroundCurrent();
  }

  getPagesAroundCurrent(): number[] {
    const pages: number[] = [];
    const startPage = Math.max(2, this.currentPage - 2);
    const endPage = Math.min(this.totalPages - 1, this.currentPage + 2);
    for (let page = startPage; page <= endPage; page++) {
      pages.push(page);
    }
    return pages;
  }

  prevPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.goToPage(this.currentPage);
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.goToPage(this.currentPage);
    }
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;

      this.loading = true;
      this.dataPage = undefined;
      this.routeParams.pageNum = this.currentPage;
      this.routeParams.pageSize = this.showPerPageControl.value;
      this.route.emit(this.routeParams);
    }
  }

  toggleSort(column: Column<T>) {
    const sortField = column.sortSource || column.field;
    const sort = this.routeParams.sort?.find((s) => s.field === sortField);

    if (sort) {
      if (sort.dir === 'asc') {
        sort.dir = 'desc';
      } else if (sort.dir === 'desc') {
        this.routeParams.sort = this.routeParams.sort?.filter((s) => s.field !== sortField);
      } else {
        sort.dir = 'asc';
      }
    } else {
      this.routeParams.sort?.push({ field: sortField, dir: 'asc' });
    }

    this.loading = true;
    this.route.emit(this.routeParams);
  }

  sortIcon(column: Column<T>): any {
    const sortField = column.sortSource || column.field;
    const sort = this.routeParams.sort?.find((s) => s.field === sortField);
    const direction = sort?.dir;

    if (column.type == 'number') {
      if (direction == 'asc') {
        return ArrowDown01;
      } else if (direction == 'desc') {
        return ArrowDown10;
      } else {
        return ArrowUpDown;
      }
    }
    if (column.type == 'date') {
      if (direction == 'asc') {
        return ArrowDownNarrowWide;
      } else if (direction == 'desc') {
        return ArrowDownWideNarrow;
      } else {
        return ArrowUpDown;
      }
    } else {
      if (direction == 'asc') {
        return ArrowDownAZ;
      } else if (direction == 'desc') {
        return ArrowDownZA;
      } else {
        return ArrowUpDown;
      }
    }
  }

  getValue(item: any, column: Column<T>) {
    if (column.callback) {
      return column.callback(item);
    } else if (column.type == 'date') {
      if (!item[column.field]) {
        return '';
      }
      const date = new Date(item[column.field]);
      return date.toLocaleDateString(column.dateFormat ?? 'en-GB');
    } else {
      return item[column.field];
    }
  }

  generateRouterLink(template: string, item: any): string {
    return template.replace(/:([a-zA-Z_]+)/g, (_, key) => item[key] || '');
  }

  handleRoute(item: any) {
    this.router.navigateByUrl(this.generateRouterLink(this.rowRouterLinkTemplate || '', item));
  }
}
